// How many lines the headline of a source shows before "+N"
export const HEADLINE_LINE_LIMIT = 3

// How many lines the opened source shows before "Show more"
export const VISIBLE_LINE_LIMIT = 5

/**
 * The lines of a source. The `line` of a source is either one line ("L3001_rngvm00604.de") or a list of lines
 * written as a Python list ("['L3001_rngvm00604.de', 'Line3002_rngvm00604.de']"). Only a value in brackets
 * is split, so a single line with a comma in its name stays one line.
 */
export const parseSourceLines = (value: unknown): string[] => {
    if (typeof value !== 'string' || value === 'undefined') return []

    const trimmed = value.trim()
    if (!trimmed.startsWith('[') || !trimmed.endsWith(']')) return trimmed ? [trimmed] : []

    return trimmed
        .slice(1, -1)
        .split(',')
        .map((line) => line.trim().replace(/^['"]|['"]$/g, '').trim())
        .filter(Boolean)
}

/**
 * Whether a line of a source is in the line filter. A line of the filter matches the same name or the name with a
 * host ("Line5004" matches "Line5004_rngvm00604.de").
 */
export const isSelectedLine = (line: string, selectedLines: string[]): boolean => {
    const name = line.toLowerCase()
    return selectedLines.some((selectedLine) => {
        const selected = selectedLine.trim().toLowerCase()
        return name === selected || name.startsWith(`${selected}_`)
    })
}

/**
 * The lines of the line filter first, then the others; both keep the order of the source.
 */
export const sortSelectedLinesFirst = (lines: string[], selectedLines: string[]): string[] => {
    if (selectedLines.length === 0) return lines

    const isSelected = (line: string) => isSelectedLine(line, selectedLines)

    return [...lines.filter(isSelected), ...lines.filter((line) => !isSelected(line))]
}

/**
 * The lines for the headline: the first ones and the number of the others ("L1, L2, L3 +25"), the lines of the
 * line filter first. A value without lines (also an empty list "[]") gives undefined, so that the headline shows N/A.
 */
export const formatSourceLinesHeadline = (value: unknown, selectedLines: string[] = []): string | undefined => {
    const lines = sortSelectedLinesFirst(parseSourceLines(value), selectedLines)
    if (lines.length === 0) return undefined

    const hiddenCount = lines.length - HEADLINE_LINE_LIMIT
    const shownLines = lines.slice(0, HEADLINE_LINE_LIMIT).join(', ')

    return hiddenCount > 0 ? `${shownLines} +${hiddenCount}` : shownLines
}
